export type RichClipboardPayload = {
  text: string;
  html: string;
};

export type RichClipboardCopyResult = "rich" | "plain" | "failed";

/**
 * Most reliable path for Outlook/Word/Gmail: fire a real copy event and
 * attach both text/html and text/plain. ClipboardItem alone often falls back
 * to plain-only in desktop mail clients.
 */
function copyViaCopyEvent(payload: RichClipboardPayload, includeHtml = true): boolean {
  let succeeded = false;

  const onCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    if (includeHtml) event.clipboardData.setData("text/html", payload.html);
    event.clipboardData.setData("text/plain", payload.text);
    event.preventDefault();
    succeeded = true;
  };

  document.addEventListener("copy", onCopy);
  try {
    // Some browsers refuse execCommand('copy') without a live selection.
    const probe = document.createElement("span");
    probe.textContent = payload.text;
    probe.setAttribute("aria-hidden", "true");
    Object.assign(probe.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "pre",
      border: "0",
    });
    document.body.appendChild(probe);

    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(probe);
    selection?.removeAllRanges();
    selection?.addRange(range);

    const ok = document.execCommand("copy");
    selection?.removeAllRanges();
    document.body.removeChild(probe);
    return ok && succeeded;
  } catch {
    return false;
  } finally {
    document.removeEventListener("copy", onCopy);
  }
}

async function copyViaClipboardItem(
  payload: RichClipboardPayload
): Promise<boolean> {
  if (
    typeof ClipboardItem === "undefined" ||
    !navigator.clipboard ||
    !("write" in navigator.clipboard)
  ) {
    return false;
  }

  // Safari requires Promise values; Chromium accepts Blobs either way.
  const item = new ClipboardItem({
    "text/html": Promise.resolve(
      new Blob([payload.html], { type: "text/html" })
    ),
    "text/plain": Promise.resolve(
      new Blob([payload.text], { type: "text/plain" })
    ),
  });
  await navigator.clipboard.write([item]);
  return true;
}

/** Copy rich HTML + plain-text fallback. Prefer this over writeText alone. */
export async function copyRichTextToClipboard(
  payload: RichClipboardPayload
): Promise<RichClipboardCopyResult> {
  if (copyViaCopyEvent(payload)) {
    return "rich";
  }

  try {
    if (await copyViaClipboardItem(payload)) {
      return "rich";
    }
  } catch {
    // Fall through to plain text.
  }

  try {
    await navigator.clipboard.writeText(payload.text);
    return "plain";
  } catch {
    return "failed";
  }
}

/** LinkedIn's composer consumes plain text more reliably than clipboard HTML. */
export async function copyPlainTextToClipboard(
  value: string
): Promise<RichClipboardCopyResult> {
  try {
    await navigator.clipboard.writeText(value);
    return "plain";
  } catch {
    return copyViaCopyEvent({ text: value, html: "" }, false)
      ? "plain"
      : "failed";
  }
}

/** Escape plain text and preserve line breaks as simple HTML paragraphs. */
export function plainTextToClipboardHtml(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map(
      (block) =>
        `<p style="margin:0 0 10px;font-size:13px;line-height:1.5;color:#334155;">${block.replace(
          /\n/g,
          "<br>"
        )}</p>`
    )
    .join("");

  return `<div style="font-family:Segoe UI,Arial,sans-serif;color:#0f172a;">${paragraphs}</div>`;
}

export function buildPlainClipboardPayload(text: string): RichClipboardPayload {
  return {
    text,
    html: plainTextToClipboardHtml(text),
  };
}
